import { LANG_META, type Lang, type LocalizedText } from '@lp/shared';

export type FieldErrors = Record<string, string>;

interface LooseIssue {
  code: string;
  path: ReadonlyArray<PropertyKey>;
  message: string;
  type?: string;
  minimum?: number | bigint;
  maximum?: number | bigint;
  validation?: unknown;
  received?: unknown;
  unionErrors?: ReadonlyArray<{ issues: ReadonlyArray<LooseIssue> }>;
}

function translate(issue: LooseIssue): string {
  switch (issue.code) {
    case 'too_small':
      if (issue.type === 'string') return Number(issue.minimum) <= 1 ? '必須填寫' : `最少 ${issue.minimum} 個字元`;
      if (issue.type === 'array') return `最少選擇 ${issue.minimum} 項`;
      return `不可少於 ${issue.minimum}`;
    case 'too_big':
      if (issue.type === 'string') return `最多 ${issue.maximum} 個字元`;
      if (issue.type === 'array') return `最多 ${issue.maximum} 項`;
      return `不可大於 ${issue.maximum}`;
    case 'invalid_string':
      if (issue.validation === 'email') return '電郵格式不正確';
      if (issue.validation === 'url') return '網址格式不正確，請以 https:// 開頭';
      if (issue.validation === 'datetime') return '日期時間格式不正確';
      return '格式不正確';
    case 'invalid_type':
      if (issue.received === 'undefined' || issue.received === 'null') return '必須填寫';
      if (issue.received === 'nan') return '請輸入數字';
      return '格式不正確';
    case 'invalid_enum_value':
      return '請選擇有效選項';
    case 'not_multiple_of':
    case 'invalid_number':
      return '請輸入整數';
    case 'invalid_union': {
      const inner = issue.unionErrors?.[0]?.issues[0];
      return inner ? translate(inner) : '格式不正確';
    }
    default:
      return '格式不正確';
  }
}

/** The part of a zod schema this helper needs. */
export interface Schema<T> {
  safeParse(v: unknown): { success: true; data: T } | { success: false; error: { issues: ReadonlyArray<unknown> } };
}

/**
 * Run a shared zod schema and turn its issues into `{ "path.to.field": "中文訊息" }`.
 * `messages` overrides the text for particular paths (e.g. a regex rule that needs explaining).
 */
export function validate<T>(
  schema: Schema<T>,
  value: unknown,
  messages: Record<string, string> = {},
): { ok: true; data: T; errors: FieldErrors } | { ok: false; errors: FieldErrors } {
  const r = schema.safeParse(value);
  if (r.success) return { ok: true, data: r.data, errors: {} };
  const errors: FieldErrors = {};
  for (const raw of r.error.issues) {
    const issue = raw as LooseIssue;
    const key = issue.path.map(String).join('.');
    if (!(key in errors)) errors[key] = messages[key] ?? translate(issue);
  }
  return { ok: false, errors };
}

/** The error for a field, including errors reported on its sub-paths (e.g. `title` picks up `title.en`). */
export function fieldError(errors: FieldErrors, key: string): string | undefined {
  if (errors[key]) return errors[key];
  const hit = Object.keys(errors).find((k) => k.startsWith(`${key}.`));
  return hit ? errors[hit] : undefined;
}

/** Adds an error when the brand's default language is empty for a required localized field. */
export function requireDefaultLang(errors: FieldErrors, key: string, value: LocalizedText, defaultLang: Lang) {
  if (!value[defaultLang]?.trim() && !errors[key]) errors[key] = `請填寫${LANG_META[defaultLang].label}內容`;
}
